import { useTranslation } from 'react-i18next';
import { Alert, Typography } from '@mui/material';

import { formatMoney } from '../money';
import type { BookingPriceView } from '../bookingPrice';

interface Props {
  /** El precio del momento, ya resuelto (`resolveBookingPrice`). */
  price: BookingPriceView;
}

/**
 * El reprecio del proveedor, al tope del momento Reservado.
 *
 * Se muestra SIEMPRE que el congelado difiera de lo que el asesor tenía
 * delante al apretar Reservar, sin umbral: es plata del cliente, y una
 * diferencia escondida es exactamente cómo se confirma un precio que ya no
 * existe. Los dos importes se muestran enteros —el anterior tachado y el
 * nuevo— y la diferencia NO se calcula ni se muestra: el frontend formatea,
 * no hace cuentas.
 *
 * Debajo, el desglose que el proveedor publica para explicar ese salto
 * (`taxes` del contrato), en una línea compacta: son renglones de lectura, no
 * una tabla. Se muestran con su propio total (el precio congelado del
 * proveedor) y NUNCA se suman entre sí: el total autoritativo es el del
 * proveedor, no la cuenta que podríamos hacer nosotros. Sin desglose el
 * callout queda con los dos importes y nada más — sin huecos.
 */
export function BookingRepriceNotice({ price }: Props) {
  const { t } = useTranslation();

  if (!price.reprice) return null;

  return (
    <Alert severity="warning">
      <Typography variant="body2">
        {t('travelServices.booking.reprice.title')}
      </Typography>
      <Typography variant="body2">
        <s>{formatMoney(price.reprice.previous)}</s> →{' '}
        <strong>{formatMoney(price.reprice.current)}</strong>
      </Typography>
      {price.quotedTaxes.length > 0 && price.quoted && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('travelServices.booking.reprice.breakdown', {
            lines: price.quotedTaxes
              .map((tax) => {
                const amount = formatMoney(tax.amount);
                return tax.name ? `${tax.name} ${amount}` : amount;
              })
              .join(' · '),
            total: formatMoney(price.quoted),
          })}
        </Typography>
      )}
    </Alert>
  );
}
